import { format, isSameDay } from "date-fns";
import { Pencil, Trash2 } from "lucide-react";
import { DateAvailability, TimeSlot } from "./types";

const timeToMinutes = (t: string) => {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
};

const formatTo12h = (time: string) => {
  const [hh, mm] = time.split(":").map(Number);
  const suffix = hh >= 12 ? "PM" : "AM";
  const hour = hh % 12 === 0 ? 12 : hh % 12;
  return `${hour}:${String(mm).padStart(2, "0")} ${suffix}`;
};

/**
 * Optional: merge overlapping or touching slots to keep summary clean
 * Example: 9-1 and 1-2 -> becomes 9-2
 */
const mergeSlots = (slots: TimeSlot[]) => {
  const sorted = [...slots]
    .filter((s) => s.start && s.end)
    .sort((a, b) => timeToMinutes(a.start) - timeToMinutes(b.start));

  const merged: { start: string; end: string }[] = [];

  for (const s of sorted) {
    if (merged.length === 0) {
      merged.push({ start: s.start, end: s.end });
      continue;
    }

    const last = merged[merged.length - 1];
    const lastEnd = timeToMinutes(last.end);
    const curStart = timeToMinutes(s.start);
    const curEnd = timeToMinutes(s.end);

    // overlap or touching
    if (curStart <= lastEnd) {
      if (curEnd > lastEnd) last.end = s.end;
    } else {
      merged.push({ start: s.start, end: s.end });
    }
  }

  return merged;
};

export default function SelectedSlotsList({
  availability,
  onEditDate,
  onDeleteDate, // delete whole day (recommended for this summary view)
}: {
  availability: DateAvailability[];
  onEditDate: (date: Date) => void;
  onDeleteDate: (date: Date) => void;
}) {
  const groups = availability
    .slice()
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .filter((a) => a.isAllDay || (a.timeSlots?.length || 0) > 0);

  if (groups.length === 0) return null;

  return (
    <div className="space-y-2">
      <p className="text-xs text-[#667085]">Selected Slots</p>

      <div className="space-y-2">
        {groups.map((day) => {
          const dateObj = new Date(day.date);

          let summary = "Available all day";
          if (day.isAllDay) summary = "Available all day";
          else if (!day.isAllDay) {
            // ✅ Use mergeSlots() to make output cleaner
            const merged = mergeSlots(day.timeSlots || []);
            summary = merged
              .map((s) => `${formatTo12h(s.start)} – ${formatTo12h(s.end)}`)
              .join(", ");
          }

          return (
            <div
              key={dateObj.toISOString()}
              className="flex items-start justify-between gap-3 py-3 px-4 border border-[#EAECF0] rounded-xl bg-white"
            >
              <div className="space-y-1">
                <div className="text-sm font-semibold text-[#101828]">
                  {format(dateObj, "EEE, do MMM")}
                </div>
                <div className="text-sm text-[#475467] leading-relaxed">
                  {summary}
                </div>
              </div>

              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => onEditDate(dateObj)}
                  className="p-2 hover:bg-muted rounded-md"
                  aria-label="Edit date"
                >
                  <Pencil className="w-4 h-4" />
                </button>

                <button
                  type="button"
                  onClick={() => onDeleteDate(dateObj)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-md"
                  aria-label="Delete date"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
